import { MapPin, Phone, MessageCircle, Instagram, Clock, Heart } from "lucide-react";
import { BUSINESS } from "@/data/salonData";
import { openWhatsApp } from "@/lib/whatsapp";
import { Reveal } from "@/lib/useReveal";

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 md:py-28 bg-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal className="text-center max-w-3xl mx-auto">
          <span className="heading-flourish">Visit The Studio</span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2a1b18] font-medium mt-4 leading-tight">
            Come Say <span className="text-gradient-rose italic">Hello</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-12 gap-8">
          {/* Info cards */}
          <Reveal className="lg:col-span-5 space-y-4">
            <a
              href={`tel:${BUSINESS.phone}`}
              data-testid="contact-phone-card"
              className="card-lift block bg-white border border-[#d98a99]/15 rounded-2xl p-5 flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#fff0f5] to-[#f9d0da] flex items-center justify-center flex-shrink-0">
                <Phone size={20} className="text-[#b56477]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4af37] font-bold">Call</div>
                <div className="font-display text-xl text-[#2a1b18] font-semibold">
                  {BUSINESS.phone}
                </div>
              </div>
            </a>

            <button
              onClick={() => openWhatsApp("Hello Anshi's Makeover!")}
              data-testid="contact-whatsapp-card"
              className="card-lift w-full text-left bg-white border border-[#d98a99]/15 rounded-2xl p-5 flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#dff7e6] to-[#a8e6b8] flex items-center justify-center flex-shrink-0">
                <MessageCircle size={20} className="text-[#25D366]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4af37] font-bold">WhatsApp</div>
                <div className="font-display text-xl text-[#2a1b18] font-semibold">
                  Chat Instantly
                </div>
              </div>
            </button>

            <a
              href={BUSINESS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram-card"
              className="card-lift block bg-white border border-[#d98a99]/15 rounded-2xl p-5 flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#fff0f5] via-[#f9d0da] to-[#d4af37] flex items-center justify-center flex-shrink-0">
                <Instagram size={20} className="text-white" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4af37] font-bold">Instagram</div>
                <div className="font-display text-xl text-[#2a1b18] font-semibold">
                  @anshi_makeover_ujjain
                </div>
              </div>
            </a>

            <div
              data-testid="contact-address-card"
              className="card-lift bg-white border border-[#d98a99]/15 rounded-2xl p-5 flex items-start gap-4"
            >
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#fff0f5] to-[#f9d0da] flex items-center justify-center flex-shrink-0">
                <MapPin size={20} className="text-[#b56477]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4af37] font-bold">Address</div>
                <div className="text-[#2a1b18] font-medium leading-snug mt-0.5">
                  {BUSINESS.address}
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#fff0f5] to-[#f9d0da]/40 border border-[#d98a99]/20 rounded-2xl p-5 flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                <Clock size={20} className="text-[#b56477]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4af37] font-bold">Open Hours</div>
                <div className="text-[#2a1b18] font-medium leading-snug mt-0.5">
                  Mon – Sun · 10:00 AM – 9:00 PM
                </div>
              </div>
            </div>
          </Reveal>

          {/* Map */}
          <Reveal className="lg:col-span-7" delay={150}>
            <div className="rounded-3xl overflow-hidden border border-[#d98a99]/20 shadow-lg h-full min-h-[420px]">
              <iframe
                src={BUSINESS.mapEmbed}
                title="Anshi's Makeover Location"
                width="100%"
                height="100%"
                style={{ minHeight: 420, border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative pt-16 pb-10 bg-[#2a1b18] text-white/80 overflow-hidden"
    >
      <div className="absolute inset-0 -z-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-gradient-radial from-[#d4af37]/20 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-gradient-radial from-[#d98a99]/20 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid md:grid-cols-3 gap-8 pb-10">
          <div>
            <div className="font-display text-2xl text-white font-semibold">
              {BUSINESS.name}
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mt-1">
              {BUSINESS.tagline}
            </div>
            <p className="mt-4 text-sm text-white/60 leading-relaxed">
              Where luxury meets tradition. A premium beauty studio crafted for
              women who refuse to compromise.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#d4af37] font-bold mb-3">
              Visit
            </div>
            <p className="text-sm leading-relaxed text-white/70">
              {BUSINESS.address}
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#d4af37] font-bold mb-3">
              Reach
            </div>
            <a href={`tel:${BUSINESS.phone}`} className="block text-sm hover:text-white text-white/80">
              {BUSINESS.phone}
            </a>
            <a
              href={BUSINESS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-sm mt-1.5 hover:text-white text-white/80"
            >
              @anshi_makeover_ujjain
            </a>
          </div>
        </div>
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/50">
          <div>
            © {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.
          </div>
          <div className="flex items-center gap-1.5">
            Made with <Heart size={11} fill="#d98a99" className="text-[#d98a99]" /> in Ujjain
          </div>
        </div>
      </div>
    </footer>
  );
}
